<template>
  <div class="age-input-dialog">
    <v-form ref="form" v-model="isFormValid">
      <v-row class="age-input-dialog-rows">
        <v-col cols="12">
          <v-text-field
            type="text"
            placeholder="+0-000-000-0000"
            persistent-placeholder
            v-model="formData.phoneNumber"
            label="Phone Number"
            :rules="phoneRules"></v-text-field>
        </v-col>
      </v-row>

      <v-select
        :menu-props="{ offsetY: true, offsetOverflow: true }"
        v-model="formData.ageInputType"
        filled
        label="Age input display type"
        :items="ageInputTypes">
        <template v-slot:append>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M7.41 8.58008L12 13.1701L16.59 8.58008L18 10.0001L12 16.0001L6 10.0001L7.41 8.58008Z"
              fill="#939099" />
          </svg>
        </template>
      </v-select>

      <v-checkbox :label="'Display medical condition question'" v-model="formData.showSpapQuestion" color="primary">
      </v-checkbox>

      <v-checkbox :label="'Enable audio'" v-model="formData.audioEnabled" color="primary"></v-checkbox>

      <div v-if="formData.audioEnabled" class="audio-uploads-section mb-6">
        <h4 class="mb-3">Audio Files</h4>

        <VaAudioFileInput
          :label="'Initial Page Audio'"
          :file-id="initialAudioFileId"
          :file-url="initialAudioFileUrl"
          @update:file="updateInitialAudioFile"
          :file-name="initialAudioFileName"
          class="mb-4"></VaAudioFileInput>

        <VaAudioFileInput
          :label="'Thank You Page Audio'"
          :file-id="thankYouAudioFileId"
          :file-url="thankYouAudioFileUrl"
          @update:file="updateThankYouAudioFile"
          :file-name="thankYouAudioFileName"></VaAudioFileInput>
      </div>
      <div v-for="(question, index) in formData.questions" :key="index" class="question-container">
        <div class="d-flex align-center mb-4">
          <v-text-field
            type="text"
            v-model="question.text"
            label="Question Text"
            class="flex-grow-1 mr-2"
            :rules="[v => !!v || 'Question text is required']"
            required></v-text-field>
          <v-btn icon color="error" @click="removeQuestion(index)" class="mt-1">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>

        <div class="ml-4">
          <v-text-field
            type="text"
            v-model="question.yesRedirectUrl"
            persistent-placeholder
            placeholder="Keep empty to move to next step"
            label="Yes Redirect URL"
            :rules="urlRules"></v-text-field>
          <v-text-field
            type="text"
            v-model="question.noRedirectUrl"
            persistent-placeholder
            placeholder="Keep empty to move to next step"
            label="No Redirect URL"
            :rules="urlRules"></v-text-field>
        </div>
      </div>

      <v-btn color="primary" class="mt-4" block @click="addQuestion">
        <v-icon left>mdi-plus</v-icon>
        Add Question
      </v-btn>

      <v-alert v-if="errorMessage" type="error" class="mt-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  const DEFAULT_AGE_INPUT_TYPES = [
    { value: "age", text: "Age" },
    { value: "birth-year", text: "Birth Year" },
    { value: "birth-year-underscore", text: "Birth Year (underscore)" },
  ];

  const AUDIO_FILE = {
    fileID: "",
    fileURL: "",
    fileName: "",
  };

  import { urlValidation } from "@/utility/urlValidation";
  import { AGE_INPUT_EVENT_TYPES } from "@/constant";
  import VaAudioFileInput from "@/components/custom/VaAudioFileInput";
  export default {
    components: {
      VaAudioFileInput,
    },
    data() {
      return {
        isFormValid: false,
        ageInputTypes: DEFAULT_AGE_INPUT_TYPES,
        formData: {
          phoneNumber: "",
          ageInputType: "age",
          showSpapQuestion: false,
          audioEnabled: false,
          initialPageAudio: { ...AUDIO_FILE },
          thankYouPageAudio: { ...AUDIO_FILE },
          questions: [],
        },
        errorMessage: "",
        phoneRules: [
          v => !!v || this.$t("generic.validation.required"),
          v => {
            const phoneRegex = /^[0-9+()-\s]+$/;

            return phoneRegex.test(v) || this.$t("generic.validation.invalid_phone");
          },
        ],
        urlRules: [v => !v || !!urlValidation(v) || this.$t("generic.validation.url")],
      };
    },
    computed: {
      initialAudioFileId() {
        return this.formData.initialPageAudio?.fileID || "";
      },
      initialAudioFileName() {
        return this.formData.initialPageAudio?.fileName || "";
      },
      initialAudioFileUrl() {
        return this.formData.initialPageAudio?.fileURL || "";
      },
      thankYouAudioFileId() {
        return this.formData.thankYouPageAudio?.fileID || "";
      },
      thankYouAudioFileName() {
        return this.formData.thankYouPageAudio?.fileName || "";
      },
      thankYouAudioFileUrl() {
        return this.formData.thankYouPageAudio?.fileURL || "";
      },
    },
    watch: {
      isFormValid: {
        handler() {
          this.sendError();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      addQuestion() {
        this.formData.questions.push({
          text: "",
          yesRedirectUrl: "",
          noRedirectUrl: "",
        });
      },
      removeQuestion(index) {
        this.formData.questions.splice(index, 1);
      },
      updateInitialAudioFile(fileData) {
        if (fileData) {
          this.$set(this.formData, "initialPageAudio", {});
          this.formData.initialPageAudio.fileID = fileData["@id"];
          this.formData.initialPageAudio.fileURL = fileData.contentUrl;
          this.formData.initialPageAudio.fileName = fileData.fileName;
        } else {
          this.formData.initialPageAudio = { ...AUDIO_FILE };
        }
      },
      updateThankYouAudioFile(fileData) {
        if (fileData) {
          this.$set(this.formData, "thankYouPageAudio", {});
          this.formData.thankYouPageAudio.fileID = fileData["@id"];
          this.formData.thankYouPageAudio.fileURL = fileData.contentUrl;
          this.formData.thankYouPageAudio.fileName = fileData.fileName;
        } else {
          this.formData.thankYouPageAudio = { ...AUDIO_FILE };
        }
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isFormValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      sendError() {
        window.parent.postMessage(
          {
            mceAction: AGE_INPUT_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isFormValid,
            },
          },
          origin,
        );
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: AGE_INPUT_EVENT_TYPES.UPDATE_AGE_INPUT_DATA,
            data: this.formData,
          },
          origin,
        );
      },

      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === AGE_INPUT_EVENT_TYPES.NEED_VALIDATION) {
            await this.$refs.form.validate();
          }
          if (message.data.type === AGE_INPUT_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            //Set default values if user exits toast window without saving (prevents JSON error)
            if (data === "undefined") {
              this.updateValue();
            } else {
              this.formData = {
                phoneNumber: data.phoneNumber,
                ageInputType: data.ageInputType,
                showSpapQuestion: data.showSpapQuestion,
                audioEnabled: data.audioEnabled,
                initialPageAudio: data.initialPageAudio || { ...AUDIO_FILE },
                thankYouPageAudio: data.thankYouPageAudio || { ...AUDIO_FILE },
                questions: data.questions || [],
              };
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: AGE_INPUT_EVENT_TYPES.REQUEST_DATA,
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  ::v-deep {
    .va-array-input__actions-wrapper {
      top: calc(50% + 14px);
      transform: translateY(-50%);
    }

    .va-array-input__item-wrapper {
      flex-direction: column !important;
      &:not(:first-child) {
        border-bottom: 2px dotted;
      }
    }
  }

  .age-input-dialog {
    margin-bottom: 30px;
    padding: 30px 12px 0px;
  }

  .age-input-dialog-rows {
    margin-bottom: 15px;
  }

  .question-container {
    margin-bottom: 24px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
  }

  .audio-uploads-section {
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.02);

    h4 {
      color: rgba(0, 0, 0, 0.87);
      font-weight: 500;
    }
  }
</style>
